import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryDestinationsMapData = (
  | Content.Properties.MapHotelEntry
  | Content.Properties.MapPoiEntry
)[];

interface UseQueryDestinationsMapOptions {
  initialData?: UseQueryDestinationsMapData;
}

interface UseQueryDestinationsMapParameters {
  destinationId: number | null;
  selectedFilters: any[];
}

const useQueryDestinationsMap = (
  { destinationId, selectedFilters }: UseQueryDestinationsMapParameters,
  options?: UseQueryDestinationsMapOptions,
) => {
  const locale = getLocale();
  const { hotelDetails } = usePageDetailsContext();
  const hotelId = hotelDetails?.hotelId ?? '';

  const query = useQuery<UseQueryDestinationsMapData>(
    [QueryId.DESTINATIONS_MAP, destinationId, selectedFilters, locale],
    async () => {
      const fetchResponse = await fetch(
        `/api/destinations/map?destinationId=${destinationId}&filters=${selectedFilters}&hotelId=${hotelId}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .then(response => response.result.entries)
        .catch(error => {
          console.error('hooks.useQueryDestinationsMap', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!destinationId,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryDestinationsMap;
